import React from 'react';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import { Loader } from 'ui/loader';
import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';

import Icons from 'assets/icons';

import type { QuickSummaryData } from './use-quick-summary';

interface SummaryRowProps {
  label: string;
  value: string;
}

const SummaryRow = ({ label, value }: SummaryRowProps): React.JSX.Element => (
  <Stack
    direction="row"
    sx={{
      alignItems: 'flex-start',
      gap: 0.75,
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      color: 'tokens.gray.700',
    }}
  >
    <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>
      <TruncatedCell value={label} />
    </Box>
    <span className="shrink-0 text-right">{value}</span>
  </Stack>
);

interface QuickSummaryPanelProps {
  data: QuickSummaryData | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  panelRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onFullResults: () => void;
}

const QuickSummaryPanel = ({
  data,
  isLoading,
  isError,
  errorMessage,
  panelRef,
  onClose,
  onFullResults,
}: QuickSummaryPanelProps): React.JSX.Element => (
  <Box
    ref={panelRef}
    sx={{
      position: 'absolute',
      right: 0,
      top: '100%',
      zIndex: 10,
      width: '300px',
      borderRadius: '0.375rem',
      borderWidth: '1px',
      borderColor: 'tokens.navy.100',
      bgcolor: 'common.white',
      p: 2.5,
      boxShadow: 8,
    }}
  >
    <Stack
      direction="row"
      sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
    >
      <h3 className="text-base font-semibold leading-6 text-navy-800">
        Awarding summary
      </h3>
      <IconButton
        size="xs"
        variant="reserve-text"
        icon={<Icons.Close />}
        onClick={onClose}
        className="-mr-2 -mt-2"
        aria-label="Close summary"
        data-testid="quick-summary-close"
      />
    </Stack>

    <div className="relative max-h-[700px] overflow-y-auto scrollbar-thin">
      {isLoading && (
        <Stack
          direction="row"
          sx={{
            minHeight: '100px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader loading overlay="white" backdrop="white" fullScreen={false} />
        </Stack>
      )}

      {!isLoading && isError && (
        <p className="pt-5 text-center text-sm text-critical-600">
          {errorMessage}
        </p>
      )}

      <div
        className="grid transition-[grid-template-rows] duration-300"
        style={{
          gridTemplateRows: !isLoading && !isError && data ? '1fr' : '0fr',
        }}
        aria-live="polite"
        role="status"
      >
        <Box sx={{ overflow: 'hidden' }}>
          {data && (
            <Stack sx={{ gap: 2, pt: 2 }}>
              <Stack sx={{ gap: 1 }}>
                <p className="text-sm font-semibold leading-5 text-gray-700">
                  Future Year Projections
                </p>
                <SummaryRow
                  label="Estimated Enrollment"
                  value={data.estimatedEnrollment}
                />
                <SummaryRow label="Estimated NTR" value={data.estimatedNtr} />
                <SummaryRow label="Average NTR" value={data.estimatedAvgNtr} />
                <SummaryRow
                  label="Estimated Discount Rate"
                  value={data.estimatedDiscountRate}
                />
                {data.enrollmentChange && (
                  <SummaryRow
                    label="Change in enrollment"
                    value={data.enrollmentChange}
                  />
                )}
              </Stack>

              {data.hasActualData && (
                <Stack sx={{ gap: 1 }}>
                  <p className="text-sm font-semibold leading-5 text-gray-700">
                    Current Year Outcomes
                  </p>
                  <SummaryRow
                    label="Number Enrolled"
                    value={data.actualEnrollment}
                  />
                  <SummaryRow label="NTR" value={data.actualNtr} />
                  <SummaryRow label="Average NTR" value={data.actualAvgNtr} />
                  <SummaryRow
                    label="Tuition Discount"
                    value={data.actualDiscountRate}
                  />
                </Stack>
              )}

              {data.hasTuitionData && (
                <Stack sx={{ gap: 1 }}>
                  <p className="text-sm font-semibold leading-5 text-gray-700">
                    Tuition &amp; Fees
                  </p>
                  <p className="text-sm">{data.tuitionCurrentYearLabel}</p>
                  {data.tuitionCurrentYearGroups.map((group) => (
                    <SummaryRow
                      key={group.name}
                      label={group.name}
                      value={group.value}
                    />
                  ))}
                  <p className="text-sm">{data.tuitionFutureYearLabel}</p>
                  {data.tuitionFutureYearGroups.map((group) => (
                    <SummaryRow
                      key={group.name}
                      label={group.name}
                      value={group.value}
                    />
                  ))}
                </Stack>
              )}

              <Button
                type="button"
                variant="brand-outlined"
                size="sm"
                onClick={onFullResults}
                className="w-fit shadow-sm"
                data-testid="quick-summary-full-results"
              >
                Full results
              </Button>
            </Stack>
          )}
        </Box>
      </div>
    </div>
  </Box>
);

export default QuickSummaryPanel;
